import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, 
  Info, 
  ChevronDown, 
  ChevronUp,
  CreditCard,
  Building2,
  Wallet
} from 'lucide-react';
import { calculateTotalFee, getCurrency } from '@/utils/currencies';

const FeeBreakdown = ({ 
  amount, 
  fromCurrency = 'USD',
  toCurrency = 'MXN',
  paymentMethod = 'bankTransfer',
  onPaymentMethodChange
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethod);

  if (!amount || parseFloat(amount) <= 0) {
    return (
      <Card className='border-2 border-dashed border-gray-300'>
        <CardContent className='p-6 text-center'>
          <DollarSign className='w-12 h-12 mx-auto mb-3 text-gray-400' />
          <p className='text-gray-600'>Enter an amount to see fee breakdown</p>
        </CardContent>
      </Card>
    );
  }

  const numericAmount = parseFloat(amount);
  const fees = calculateTotalFee(numericAmount, selectedMethod);
  const fromCurr = getCurrency(fromCurrency);
  const toCurr = getCurrency(toCurrency);

  const paymentMethods = [
    {
      id: 'bankTransfer',
      name: 'Bank Transfer',
      icon: Building2,
      description: 'SEPA, ACH, FedWire',
      fees: calculateTotalFee(numericAmount, 'bankTransfer'),
      recommended: true
    },
    {
      id: 'card',
      name: 'Card Payment',
      icon: CreditCard,
      description: 'Debit/Credit, Apple Pay',
      fees: calculateTotalFee(numericAmount, 'card'),
      recommended: false
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: Wallet,
      description: 'PayPal Balance',
      fees: calculateTotalFee(numericAmount, 'paypal'),
      recommended: false
    }
  ];

  const handleMethodSelect = (methodId) => {
    setSelectedMethod(methodId);
    if (onPaymentMethodChange) {
      onPaymentMethodChange(methodId);
    }
  };

  const formatAmount = (amt, currencyCode = fromCurrency) => {
    const curr = getCurrency(currencyCode);
    return `${curr?.symbol || '$'}${parseFloat(amt).toFixed(2)}`;
  };

  return (
    <div className='space-y-4'>
      {/* Payment Method Selection */}
      <Card className='border-2'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-lg flex items-center space-x-2'>
            <CreditCard className='w-5 h-5 text-emerald-600' />
            <span>Payment Method</span>
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          {paymentMethods.map(method => {
            const Icon = method.icon;
            const isSelected = selectedMethod === method.id;
            
            return (
              <button
                key={method.id}
                onClick={() => handleMethodSelect(method.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  isSelected 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 hover:border-emerald-300 bg-white'
                }`}
              >
                <div className='flex items-start justify-between'>
                  <div className='flex items-start space-x-3'>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-emerald-100' : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-emerald-600' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <div className='flex items-center space-x-2'>
                        <p className='font-semibold text-gray-900'>{method.name}</p>
                        {method.recommended && (
                          <Badge className='bg-emerald-100 text-emerald-700 text-xs'>Lowest Fee</Badge>
                        )}
                      </div>
                      <p className='text-sm text-gray-600'>{method.description}</p>
                      <p className='text-sm font-semibold text-emerald-600 mt-1'>
                        {method.fees.totalPercentage}% fee • {formatAmount(method.fees.totalFee)}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className='w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center'>
                      <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Fee Summary */}
      <Card className='border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-lg flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <DollarSign className='w-5 h-5 text-emerald-600' />
              <span>Total Cost</span>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className='flex items-center space-x-1 text-sm text-emerald-600 hover:text-emerald-700'
            >
              <span>{showDetails ? 'Hide' : 'Show'} Details</span>
              {showDetails ? (
                <ChevronUp className='w-4 h-4' />
              ) : (
                <ChevronDown className='w-4 h-4' />
              )}
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          {/* Main Amount */}
          <div className='flex justify-between items-center pb-3 border-b border-emerald-200'>
            <span className='text-gray-700'>Transfer Amount</span>
            <span className='font-semibold text-gray-900'>{formatAmount(numericAmount)}</span>
          </div>

          {/* Detailed Breakdown */}
          {showDetails && (
            <div className='space-y-2 pb-3 border-b border-emerald-200'>
              <div className='flex justify-between items-center text-sm'>
                <div className='flex items-center space-x-1'>
                  <span className='text-gray-600'>MoonPay Fee</span>
                  <div className='group relative'>
                    <Info className='w-3 h-3 text-gray-400 cursor-help' />
                    <div className='hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10'>
                      Payment provider fee for processing your transaction
                    </div>
                  </div>
                </div>
                <span className='text-gray-700'>{formatAmount(fees.moonpayFee)}</span>
              </div>

              <div className='flex justify-between items-center text-sm'>
                <div className='flex items-center space-x-1'>
                  <span className='text-gray-600'>RouteFlow AI Service</span>
                  <div className='group relative'>
                    <Info className='w-3 h-3 text-gray-400 cursor-help' />
                    <div className='hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10'>
                      Platform service fee for facilitating your transfer
                    </div>
                  </div>
                </div>
                <span className='text-gray-700'>{formatAmount(fees.routeflowFee)}</span>
              </div>

              <div className='flex justify-between items-center text-sm pt-2 border-t border-emerald-200'>
                <span className='text-gray-600'>Total Fees</span>
                <span className='font-semibold text-emerald-600'>
                  {formatAmount(fees.totalFee)} ({fees.totalPercentage}%)
                </span>
              </div>
            </div>
          )}

          {/* Total */}
          <div className='flex justify-between items-center pt-3 border-t-2 border-emerald-300'>
            <span className='text-lg font-bold text-gray-900'>You Pay</span>
            <span className='text-2xl font-bold text-emerald-600'>{formatAmount(fees.totalAmount)}</span>
          </div>

          {/* Recipient Gets */}
          {toCurrency && toCurrency !== fromCurrency && (
            <div className='bg-white rounded-lg p-3 border border-emerald-200'>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Recipient Gets</span>
                <div className='text-right'>
                  <p className='font-semibold text-gray-900'>
                    {toCurr?.symbol}{(numericAmount * (toCurr?.rate / fromCurr?.rate)).toFixed(2)} {toCurrency}
                  </p>
                  <p className='text-xs text-gray-500'>
                    Rate: 1 {fromCurrency} = {(toCurr?.rate / fromCurr?.rate).toFixed(4)} {toCurrency}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Comparison with Competitors */}
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-3'>
            <div className='flex items-start space-x-2'>
              <Info className='w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0' />
              <div className='text-xs text-amber-900'>
                <p className='font-semibold mb-1'>Save up to 60% vs traditional services</p>
                <p className='text-amber-700'>
                  Western Union/MoneyGram typically charge 8-12% for this transfer
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeeBreakdown;
